import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, ArrowLeft, Search, Filter, User, Wrench, Package, 
  DollarSign, Clock, BookOpen, Globe, Edit, Trash2, 
  Calendar, TrendingUp, Loader2 
} from 'lucide-react';

function Resources() {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [selectedType, setSelectedType] = useState('human');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'available',
    type_specific_data: {}
  });

  useEffect(() => {
    fetchProject();
    fetchResources();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error(t('projectLoadFailed'));
      navigate('/dashboard');
    }
  };

  const fetchResources = async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/resources`);
      setResources(response.data);
    } catch (error) {
      toast.error(t('resourcesLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getResourceIcon = (type) => {
    const icons = {
      human: User,
      equipment: Wrench,
      material: Package,
      financial: DollarSign,
      time: Clock,
      knowledge: BookOpen,
      digital: Globe
    };
    return icons[type] || User;
  };

  const getResourceColor = (type) => {
    const colors = {
      human: 'bg-blue-100 text-blue-800',
      equipment: 'bg-gray-100 text-gray-800',
      material: 'bg-amber-100 text-amber-800',
      financial: 'bg-green-100 text-green-800',
      time: 'bg-orange-100 text-orange-800',
      knowledge: 'bg-purple-100 text-purple-800',
      digital: 'bg-cyan-100 text-cyan-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'bg-green-100 text-green-800',
      in_use: 'bg-blue-100 text-blue-800',
      maintenance: 'bg-yellow-100 text-yellow-800',
      unavailable: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredResources = resources.filter(resource => {
    const matchesTab = activeTab === 'all' || resource.type === activeTab;
    const matchesSearch = resource.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const handleCreateResource = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Lütfen kaynak adı girin (* ile işaretli alan zorunludur)');
      return;
    }
    
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        type: selectedType,
        status: formData.status,
        type_specific_data: formData.type_specific_data
      };

      if (editingResource) {
        await axiosInstance.put(`/resources/${editingResource.id}`, payload);
        toast.success(t('resourceUpdated'));
      } else {
        await axiosInstance.post(`/projects/${projectId}/resources`, payload);
        toast.success(t('resourceCreated'));
      }

      setDialogOpen(false);
      resetForm();
      fetchResources();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('error');
      toast.error(errorMsg);
      console.error('Resource error:', error.response?.data);
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm(t('confirmDeleteResource'))) return;

    try {
      await axiosInstance.delete(`/resources/${resourceId}`);
      toast.success(t('resourceDeleted'));
      fetchResources();
    } catch (error) {
      toast.error(t('deleteFailed'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      status: 'available',
      type_specific_data: {}
    });
    setEditingResource(null);
  };

  const openEditDialog = (resource) => {
    setEditingResource(resource);
    setSelectedType(resource.type);
    setFormData({
      name: resource.name,
      description: resource.description || '',
      status: resource.status,
      type_specific_data: resource.type_specific_data || {}
    });
    setDialogOpen(true);
  };

  const renderTypeSpecificFields = () => {
    const data = formData.type_specific_data;

    switch (selectedType) {
      case 'human':
        return (
          <>
            <div className="grid gap-2">
              <Label>{t('role')}</Label>
              <Input
                value={data.role || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, role: e.target.value }
                })}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('hourlyRate')}</Label>
              <Input
                type="number"
                value={data.hourly_rate || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, hourly_rate: parseFloat(e.target.value) }
                })}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('capacity')} ({t('hoursPerWeek')})</Label>
              <Input
                type="number"
                value={data.capacity || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, capacity: parseInt(e.target.value) }
                })}
              />
            </div>
          </>
        );

      case 'equipment':
        return (
          <>
            <div className="grid gap-2">
              <Label>{t('serialNumber')}</Label>
              <Input
                value={data.serial_number || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, serial_number: e.target.value }
                })}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('costPerUse')}</Label>
              <Input
                type="number"
                value={data.cost_per_use || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, cost_per_use: parseFloat(e.target.value) }
                })}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('location')}</Label>
              <Input
                value={data.location || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, location: e.target.value }
                })}
              />
            </div>
          </>
        );

      case 'material':
        return (
          <>
            <div className="grid gap-2">
              <Label>{t('stockQuantity')}</Label>
              <Input
                type="number"
                value={data.stock_quantity || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, stock_quantity: parseFloat(e.target.value) }
                })}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('unit')}</Label>
              <Input
                value={data.unit || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, unit: e.target.value }
                })}
                placeholder={t('unitPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('unitPrice')}</Label>
              <Input
                type="number"
                value={data.unit_price || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, unit_price: parseFloat(e.target.value) }
                })}
              />
            </div>
          </>
        );

      case 'financial':
        return (
          <>
            <div className="grid gap-2">
              <Label>{t('totalBudget')}</Label>
              <Input
                type="number"
                value={data.total_budget || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, total_budget: parseFloat(e.target.value) }
                })}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('costCenter')}</Label>
              <Input
                value={data.cost_center || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  type_specific_data: { ...data, cost_center: e.target.value }
                })}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}`)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{t('resourceManagement')}</h1>
                <p className="text-sm text-gray-500">{project?.name}</p>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} style={{ background: '#3B82F6' }}>
              <Plus className="h-4 w-4 mr-2" />
              {t('addResource')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="mb-6 flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t('searchResources')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">{t('all')}</TabsTrigger>
            <TabsTrigger value="human">{t('human')}</TabsTrigger>
            <TabsTrigger value="equipment">{t('equipment')}</TabsTrigger>
            <TabsTrigger value="material">{t('material')}</TabsTrigger>
            <TabsTrigger value="financial">{t('financial')}</TabsTrigger>
            <TabsTrigger value="time">{t('time')}</TabsTrigger>
            <TabsTrigger value="knowledge">{t('knowledge')}</TabsTrigger>
            <TabsTrigger value="digital">{t('digital')}</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Resource Grid */}
        {filteredResources.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('noResourcesFound')}</h3>
            <Button onClick={() => setDialogOpen(true)} style={{ background: '#3B82F6' }} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              {t('addFirstResource')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => {
              const Icon = getResourceIcon(resource.type);
              return (
                <Card key={resource.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${getResourceColor(resource.type)}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{resource.name}</CardTitle>
                          <Badge className={`mt-1 ${getResourceColor(resource.type)}`}>
                            {t(resource.type)}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(resource)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteResource(resource.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Badge className={getStatusColor(resource.status)}>
                        {t(resource.status)}
                      </Badge>
                      {resource.description && (
                        <p className="text-sm text-gray-600">{resource.description}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingResource ? t('editResource') : t('addResource')}</DialogTitle>
            <DialogDescription>{t('resourceFormDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateResource}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>{t('resourceType')}</Label>
                <Select value={selectedType} onValueChange={setSelectedType} disabled={!!editingResource}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="human">{t('human')}</SelectItem>
                    <SelectItem value="equipment">{t('equipment')}</SelectItem>
                    <SelectItem value="material">{t('material')}</SelectItem>
                    <SelectItem value="financial">{t('financial')}</SelectItem>
                    <SelectItem value="time">{t('time')}</SelectItem>
                    <SelectItem value="knowledge">{t('knowledge')}</SelectItem>
                    <SelectItem value="digital">{t('digital')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>{t('name')} <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Kaynak adı girin..."
                />
              </div>

              <div className="grid gap-2">
                <Label>{t('description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid gap-2">
                <Label>{t('status')}</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">{t('available')}</SelectItem>
                    <SelectItem value="in_use">{t('in_use')}</SelectItem>
                    <SelectItem value="maintenance">{t('maintenance')}</SelectItem>
                    <SelectItem value="unavailable">{t('unavailable')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {renderTypeSpecificFields()}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" style={{ background: '#3B82F6' }}>
                {editingResource ? t('save') : t('create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Resources;
